/**
 * Is this a phone or a tablet?
 *
 * ---------------------------------------------------------------------------
 * WHAT IT IS FOR
 * ---------------------------------------------------------------------------
 * One thing only: deciding whether this device may PERFORM a deployment
 * transfer. A cloud-to-local move writes a pharmacy's whole patient panel into
 * this browser's IndexedDB and then empties the side it came from, so running
 * it on a phone leaves the only copy of a clinical database in the one store a
 * mobile browser may evict on its own, on the device most easily lost.
 *
 * The portal refuses the same thing from its end -- the deployment cards on the
 * data-residency page, and the hidden frame in RetroactTransferRunner.vue. This
 * is the same refusal on the origin that actually holds the records.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT VIEWPORT WIDTH
 * ---------------------------------------------------------------------------
 * Deliberately no width test, unlike the portal's `useIsMobile`, and the
 * difference is not cosmetic:
 *
 *   - The transfer also runs inside a 1x1 hidden IFRAME (the portal drives
 *     DeploymentTransferFrame.vue that way). `window.innerWidth` there is 1, so
 *     a width test would report "mobile" on every desktop and block the ordinary
 *     path completely.
 *   - A desktop window dragged narrow is not a phone, and deferring a
 *     pharmacy's transfer because someone resized a window would be wrong.
 *
 * Pointer and hover describe the DEVICE and are inherited correctly by a frame,
 * which is exactly the property needed here. `(hover: none)` is what keeps a
 * touchscreen laptop out: it reports a coarse pointer for its screen but still
 * has a fine one and real hover, so it reads as the desktop it is.
 *
 * Answers false where it cannot tell (no `window`, no `matchMedia`). A transfer
 * that is wrongly allowed is recoverable -- every refusal in migrate.js still
 * applies, and the records are still somewhere. One wrongly blocked on a
 * pharmacist's own desktop is a move that never happens and nothing on screen
 * to say why.
 */
const TOUCH_PRIMARY_QUERY = '(pointer: coarse) and (hover: none)';

export function isMobileDevice() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia(TOUCH_PRIMARY_QUERY).matches;
  } catch {
    return false;
  }
}
